import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import { Loader2, Lock, Mail, ArrowRight, MessageSquare, Phone, ImagePlus, ShieldCheck } from "lucide-react";

const OFFICE_IMG =
  "https://images.unsplash.com/photo-1758520145175-aa3b593b81af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate(res.user.role === "super_admin" ? "/admin" : "/dashboard");
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen bg-[#050a08] lg:grid-cols-2">
      {/* Brand / image panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden p-12 lg:flex grain">
        <img src={OFFICE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050a08] via-[#050a08]/70 to-[#08160f]/90" />
        <div className="absolute -right-24 top-1/3 h-96 w-96 rounded-full bg-[#2ac67b]/20 blur-[120px]" />
        <Link to="/" className="relative z-10">
          <img src={LOGO_URL} alt="SuperChat" className="h-14 w-auto object-contain" />
        </Link>
        <div className="relative z-10">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#2ac67b]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2ac67b] sc-glow-pulse" /> support os
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-white">
            Every conversation,<br /><span className="neon-text">one command center.</span>
          </h1>
          <p className="mt-4 max-w-md text-white/50">Live chat, voice calls and media — handled from a fast, modern workspace built for support teams.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {[["Live chat", MessageSquare], ["Voice calls", Phone], ["Media", ImagePlus]].map(([label, Icon]) => (
              <span key={label} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
                <Icon className="h-4 w-4 text-[#2ac67b]" aria-hidden="true" /> {label}
              </span>
            ))}
          </div>
        </div>
        <p className="relative z-10 font-mono-x text-[11px] uppercase tracking-[0.2em] text-white/30">© {new Date().getFullYear()} superchat · beta</p>
      </div>

      {/* Form panel */}
      <div className="relative flex items-center justify-center px-6 py-12 web3-dots">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <img src={LOGO_URL} alt="SuperChat" className="h-12 w-auto object-contain" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-white">Sign in</h2>
          <p className="mt-1 text-sm text-white/50">Welcome back. Let's get you to your conversations.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block font-mono-x text-[11px] uppercase tracking-[0.15em] text-white/50">Email</label>
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 transition-colors focus-within:border-[#2ac67b] focus-within:ring-1 focus-within:ring-[#2ac67b]/40">
                <Mail className="h-4 w-4 text-white/40" aria-hidden="true" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="w-full bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-white/30" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block font-mono-x text-[11px] uppercase tracking-[0.15em] text-white/50">Password</label>
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 transition-colors focus-within:border-[#2ac67b] focus-within:ring-1 focus-within:ring-[#2ac67b]/40">
                <Lock className="h-4 w-4 text-white/40" aria-hidden="true" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-white/30" data-testid="login-password-input" />
              </div>
            </div>

            {error && <div className="rounded-lg border border-[#ff2a55]/30 bg-[#ff2a55]/10 px-3 py-2 text-sm text-[#ff6b88]" data-testid="login-error">{error}</div>}

            <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2ac67b] py-3 text-sm font-semibold text-black transition-all hover:bg-[#22c55e] hover:shadow-[0_0_24px_rgba(42,198,123,0.4)] disabled:opacity-60" data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <>Sign in <ArrowRight className="h-4 w-4" aria-hidden="true" /></>}
            </button>
          </form>

          <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-sm text-white/40">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            <Link to="/" className="font-medium text-[#2ac67b] hover:underline">Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
